import { CommonModule } from '@angular/common';
import {
    ChangeDetectorRef,
    Component,
    ContentChild,
    ElementRef,
    EventEmitter,
    Input,
    NgZone,
    OnDestroy,
    OnInit,
    Output,
    TemplateRef,
} from '@angular/core';
import { MatIconModule } from '@angular/material/icon';
import {
    colSpanWithMin,
    fineColumnsForWidth,
    LAYOUT_GAP,
    MAX_ROWS,
    MIN_ROWS,
    ROW_UNIT,
    rowUnitFor,
    rowsForPx,
    stackedShapeScale,
    twelfthsForSpan,
    widthLabel,
} from '../dashboard-layout';

/** One section of a layout grid: a width in twelfths of the screen and a height in 40px rows. */
export interface LayoutGridItem {
    id: string;
    width: number;
    height: number;
    /** The fewest rows this section may shrink to (default MIN_ROWS). */
    minHeight?: number;
    /** The narrowest it may get, in px: on a narrower screen it takes more columns and the rest wrap. */
    minWidth?: number;
    /** On a page narrower than this many px, span the whole row (sections that sit side by side). */
    fullRowBelow?: number;
    /** Stacked across the whole row (fullRowBelow), grow taller as much as it got wider (a graph). */
    keepShape?: boolean;
}

export interface LayoutGridItemContext {
    $implicit: LayoutGridItem;
    index: number;
    /** Bind to a drag handle's (pointerdown) to move the section (edit mode only). */
    startDrag: (event: PointerEvent) => void;
}

/**
 * The movable, resizable grid behind the Management Tools dashboard and the Statistics page
 * (OE-265). 3-12 fine columns by width (dashboard-layout.ts) and 40px rows; in edit mode a section
 * moves by its handle and resizes from its corner. The parent owns the items and saves them:
 * `moved` and `resized` say what the person did.
 *
 *   <shared-layout-grid [items]="items" [editMode]="editing" (moved)="..." (resized)="...">
 *       <ng-template let-item let-startDrag="startDrag"> ... </ng-template>
 *   </shared-layout-grid>
 */
@Component({
    selector: 'shared-layout-grid',
    standalone: true,
    imports: [CommonModule, MatIconModule],
    template: `
        <div
            class="lg-grid"
            [style.grid-template-columns]="'repeat(' + fineColumns + ', minmax(0, 1fr))'"
            [style.grid-auto-rows.px]="rowUnit"
            [style.gap.px]="gap"
        >
            <div
                *ngFor="let item of items; let i = index; trackBy: trackById"
                class="lg-item"
                [attr.data-layout-id]="item.id"
                [class.lg-edit]="editMode"
                [class.lg-drag-source]="dragIndex === i"
                [class.lg-drop-target]="dropTargetIndex === i"
                [class.lg-resizing]="resizingId === item.id"
                [style.grid-column]="'span ' + colSpan(item)"
                [style.grid-row]="'span ' + rowSpan(item)"
            >
                <ng-container
                    *ngTemplateOutlet="itemTemplate; context: { $implicit: item, index: i, startDrag: dragStarter(i) }"
                ></ng-container>
                <span class="lg-badge" *ngIf="resizingId === item.id">{{ sizeLabel(item) }}</span>
                <div
                    class="lg-resize"
                    *ngIf="editMode"
                    aria-label="Drag to resize"
                    title="Drag to resize"
                    (pointerdown)="onResizeStart(item, $event)"
                >
                    <mat-icon class="lg-resize-icon">drag_handle</mat-icon>
                </div>
            </div>
        </div>
    `,
    styles: [
        `
            :host {
                display: block;
            }
            .lg-grid {
                display: grid;
                grid-auto-flow: dense;
                align-items: stretch;
            }
            .lg-item {
                position: relative;
                min-width: 0;
                min-height: 0;
            }
            .lg-item > :first-child {
                height: 100%;
            }
            .lg-edit {
                outline: 1px dashed rgba(25, 118, 210, 0.35);
                outline-offset: 2px;
                border-radius: 8px;
            }
            .lg-resizing {
                z-index: 10;
                user-select: none;
                outline: 2px solid #1976d2;
            }
            .lg-drag-source {
                opacity: 0.25;
            }
            .lg-drop-target {
                outline: 2px solid #1976d2;
                outline-offset: 2px;
            }
            .lg-badge {
                position: absolute;
                top: 8px;
                left: 50%;
                transform: translateX(-50%);
                z-index: 30;
                padding: 2px 10px;
                border-radius: 10px;
                background: #1976d2;
                color: #fff;
                font-size: 12px;
                font-weight: 500;
                white-space: nowrap;
                pointer-events: none;
            }
            .lg-resize {
                position: absolute;
                bottom: 0;
                right: 0;
                width: 32px;
                height: 32px;
                cursor: nwse-resize;
                display: flex;
                align-items: center;
                justify-content: center;
                border-radius: 8px 0 8px 0;
                background: rgba(25, 118, 210, 0.12);
                touch-action: none;
                z-index: 25;
            }
            .lg-resize-icon {
                font-size: 18px;
                width: 18px;
                height: 18px;
                color: #1976d2;
                transform: rotate(-45deg);
            }
        `,
    ],
})
export class LayoutGridComponent implements OnInit, OnDestroy {
    @Input() items: LayoutGridItem[] = [];
    /**
     * The px a section's content needs, by id: it never gets fewer rows than that (the Statistics
     * totals, whose pace panel was cut off once rows shrank on a narrower window).
     */
    @Input() contentHeights: Record<string, number> = {};
    @Input() editMode = false;
    @Output() moved = new EventEmitter<{ from: number; to: number }>();
    @Output() resized = new EventEmitter<{ id: string; width: number; height: number }>();

    @ContentChild(TemplateRef) itemTemplate!: TemplateRef<LayoutGridItemContext>;

    /** Follows the grid width so cards keep their shape (rowUnitFor). */
    public rowUnit = ROW_UNIT;
    public readonly gap = LAYOUT_GAP;
    public fineColumns = 12;
    private gridWidth = 0;

    public dragIndex: number | null = null;
    public dropTargetIndex: number | null = null;
    public resizingId: string | null = null;

    private observer?: ResizeObserver;
    private preview = new Map<string, { col: number; row: number }>();
    private starters = new Map<number, (e: PointerEvent) => void>();
    private dragClone: HTMLElement | null = null;
    private dragOffset = { x: 0, y: 0 };
    private dragListeners: { move: (e: PointerEvent) => void; up: () => void } | null = null;

    constructor(
        private host: ElementRef<HTMLElement>,
        private zone: NgZone,
        private cdr: ChangeDetectorRef
    ) {}

    public ngOnInit(): void {
        if (typeof ResizeObserver === 'undefined') return;
        this.observer = new ResizeObserver((entries) => {
            const width = Math.round(entries[0]?.contentRect.width || 0);
            const columns = width ? fineColumnsForWidth(width) : this.fineColumns;
            if (columns !== this.fineColumns || width !== this.gridWidth) {
                this.zone.run(() => {
                    this.fineColumns = columns;
                    this.gridWidth = width;
                    this.rowUnit = rowUnitFor(width);
                    this.cdr.markForCheck();
                });
            }
        });
        this.observer.observe(this.host.nativeElement);
    }

    public ngOnDestroy(): void {
        this.observer?.disconnect();
        this.cleanUpDrag();
    }

    public trackById(_index: number, item: LayoutGridItem): string {
        return item.id;
    }

    public colSpan(item: LayoutGridItem): number {
        return (
            this.preview.get(item.id)?.col ??
            colSpanWithMin(item.width, this.fineColumns, this.gridWidth, item.minWidth, item.fullRowBelow)
        );
    }

    public rowSpan(item: LayoutGridItem): number {
        const preview = this.preview.get(item.id)?.row;
        if (preview !== undefined) return preview;
        const rows = Math.round(Math.max(item.minHeight ?? MIN_ROWS, item.height) * this.shapeScale(item));
        return Math.min(MAX_ROWS, Math.max(rows, rowsForPx(this.contentHeights?.[item.id] ?? 0, this.rowUnit)));
    }

    private shapeScale(item: LayoutGridItem): number {
        return item.keepShape ? stackedShapeScale(item.width, this.fineColumns, this.gridWidth, item.fullRowBelow) : 1;
    }

    public sizeLabel(item: LayoutGridItem): string {
        const rows = this.rowSpan(item);
        return `${widthLabel(twelfthsForSpan(this.colSpan(item), this.fineColumns))} · ${rows} rows`;
    }

    /** A stable function per index, so the template context does not change every check. */
    public dragStarter(index: number): (e: PointerEvent) => void {
        let fn = this.starters.get(index);
        if (!fn) {
            fn = (e: PointerEvent) => this.onDragStart(e, index);
            this.starters.set(index, fn);
        }
        return fn;
    }

    private onDragStart(event: PointerEvent, index: number): void {
        if (!this.editMode) return;
        event.preventDefault();
        event.stopPropagation();
        const card = this.itemElements()[index];
        if (!card) return;

        const rect = card.getBoundingClientRect();
        this.dragOffset = { x: event.clientX - rect.left, y: event.clientY - rect.top };
        const clone = card.cloneNode(true) as HTMLElement;
        Object.assign(clone.style, {
            position: 'fixed',
            width: rect.width + 'px',
            height: rect.height + 'px',
            left: rect.left + 'px',
            top: rect.top + 'px',
            zIndex: '1000',
            pointerEvents: 'none',
            opacity: '0.85',
            boxShadow: '0 8px 28px rgba(0,0,0,0.25)',
            transform: 'rotate(1deg) scale(1.02)',
        });
        document.body.appendChild(clone);
        this.dragClone = clone;
        this.dragIndex = index;
        document.body.style.userSelect = 'none';
        this.cdr.markForCheck();

        const move = (e: PointerEvent) => {
            if (this.dragClone) {
                this.dragClone.style.left = e.clientX - this.dragOffset.x + 'px';
                this.dragClone.style.top = e.clientY - this.dragOffset.y + 'px';
            }
            const target = this.itemIndexAt(e.clientX, e.clientY);
            if (target !== this.dropTargetIndex) {
                this.zone.run(() => {
                    this.dropTargetIndex = target;
                    this.cdr.markForCheck();
                });
            }
        };
        const up = () =>
            this.zone.run(() => {
                const from = this.dragIndex;
                const to = this.dropTargetIndex;
                this.cleanUpDrag();
                if (from !== null && to !== null && from !== to) this.moved.emit({ from, to });
                this.cdr.markForCheck();
            });
        this.dragListeners = { move, up };
        this.zone.runOutsideAngular(() => {
            document.addEventListener('pointermove', move);
            document.addEventListener('pointerup', up);
        });
    }

    private cleanUpDrag(): void {
        this.dragClone?.remove();
        this.dragClone = null;
        this.dragIndex = null;
        this.dropTargetIndex = null;
        document.body.style.userSelect = '';
        if (this.dragListeners) {
            document.removeEventListener('pointermove', this.dragListeners.move);
            document.removeEventListener('pointerup', this.dragListeners.up);
            this.dragListeners = null;
        }
    }

    private itemElements(): HTMLElement[] {
        return Array.from(this.host.nativeElement.querySelectorAll(':scope > .lg-grid > .lg-item')) as HTMLElement[];
    }

    private itemIndexAt(x: number, y: number): number | null {
        const items = this.itemElements();
        for (let i = 0; i < items.length; i++) {
            if (i === this.dragIndex) continue;
            const r = items[i].getBoundingClientRect();
            if (x >= r.left && x <= r.right && y >= r.top && y <= r.bottom) return i;
        }
        return null;
    }

    public onResizeStart(item: LayoutGridItem, event: PointerEvent): void {
        if (!this.editMode) return;
        event.preventDefault();
        event.stopPropagation();
        const start = { x: event.clientX, y: event.clientY, col: this.colSpan(item), row: this.rowSpan(item) };
        const width = this.host.nativeElement.getBoundingClientRect().width;
        const cellWidth = (width - LAYOUT_GAP * (this.fineColumns - 1)) / this.fineColumns + LAYOUT_GAP;
        const cellHeight = this.rowUnit + LAYOUT_GAP;
        const minCol = colSpanWithMin(1, this.fineColumns, width, item.minWidth);
        const minRow = item.minHeight ?? MIN_ROWS;
        this.resizingId = item.id;
        this.preview.set(item.id, { col: start.col, row: start.row });
        this.cdr.markForCheck();

        const move = (e: PointerEvent) => {
            const col = Math.max(
                minCol,
                Math.min(this.fineColumns, start.col + Math.round((e.clientX - start.x) / cellWidth))
            );
            const row = Math.max(
                minRow,
                Math.min(MAX_ROWS, start.row + Math.round((e.clientY - start.y) / cellHeight))
            );
            const prev = this.preview.get(item.id);
            if (prev?.col !== col || prev?.row !== row) {
                this.zone.run(() => {
                    this.preview.set(item.id, { col, row });
                    this.cdr.markForCheck();
                });
            }
        };
        const up = () => {
            document.removeEventListener('pointermove', move);
            document.removeEventListener('pointerup', up);
            this.zone.run(() => {
                const spans = this.preview.get(item.id);
                this.preview.delete(item.id);
                this.resizingId = null;
                this.cdr.markForCheck();
                if (spans && (spans.col !== start.col || spans.row !== start.row)) {
                    this.resized.emit({
                        id: item.id,
                        width: twelfthsForSpan(spans.col, this.fineColumns),
                        // Saved at its side-by-side size: a stacked section's extra rows come back on their own.
                        height: Math.max(minRow, Math.round(spans.row / this.shapeScale(item))),
                    });
                }
            });
        };
        this.zone.runOutsideAngular(() => {
            document.addEventListener('pointermove', move);
            document.addEventListener('pointerup', up);
        });
    }
}
